import type {StoreApi} from 'zustand/vanilla';

import {isNilOrEmpty} from 'ramda-adjunct';

import {VIRTUAL_PRESET_ID} from '../../constants/VIRTUAL_PRESET_ID';
import type {ChoiceFilterOptions, SuggestionsOptions} from '../../types/Api';
import type {SelectedCellRange} from '../../types/SelectedCellRange';
import type {ToolpanelType} from '../../types/ToolpanelType';
import {
  loadPinnedPresetIds,
  savePinnedPresetIds,
  savePresetDisplayMode,
} from '../../utils/presetLocalPrefs';
import type {EagGridState, EagGridStore} from '../createEagGridStore';

type MiscUiActions = Pick<
  EagGridStore,
  | 'setPage'
  | 'setIsMaximized'
  | 'subscribeBeforeMaximizationChange'
  | 'setBulkActions'
  | 'setActivePanel'
  | 'setChoiceFilterOptions'
  | 'setSuggestionLabels'
  | 'setIsBulkActionLoading'
  | 'setSelectedCellRange'
  | 'setPresetDisplayMode'
  | 'setVirtualPresetPinned'
>;

/**
 * Residual lightweight UI-state setters that didn't merit their own slice:
 * page, active tool panel, choice-filter options cache and bulk-action loading.
 * Bodies moved verbatim from createUiActions.
 */
export function createMiscUiActions(store: StoreApi<EagGridState>): MiscUiActions {
  const maximizationListeners = new Set<(isMaximized: boolean) => void>();

  return {
    subscribeBeforeMaximizationChange: (listener) => {
      maximizationListeners.add(listener);
      return () => {
        maximizationListeners.delete(listener);
      };
    },
    setIsMaximized: (isMaximized) => {
      if (store.getState().isMaximized !== isMaximized) {
        maximizationListeners.forEach((listener) => listener(isMaximized));
        store.setState({isMaximized});
      }
    },
    setPresetDisplayMode: (mode) => {
      const state = store.getState();
      if (state.presetDisplayModeOverride === mode) {
        return;
      }
      savePresetDisplayMode(state.gridCode, mode);
      store.setState({presetDisplayModeOverride: mode});
    },

    setVirtualPresetPinned: (isPinned) => {
      const state = store.getState();
      const pinnedIds = loadPinnedPresetIds(state.gridCode);
      if (isPinned) {
        pinnedIds.add(VIRTUAL_PRESET_ID);
      } else {
        pinnedIds.delete(VIRTUAL_PRESET_ID);
      }
      savePinnedPresetIds(state.gridCode, pinnedIds);
      store.setState({isVirtualPresetPinned: isPinned});
    },

    setBulkActions: (bulkActions) => store.setState({bulkActions}),
    setPage: (page: number) => {
      if (store.getState().page === page) {
        return;
      }
      store.setState({page, rowSelectionAnchorId: null});
    },

    setActivePanel: (activePanel: ToolpanelType | null) => {
      store.setState({activePanel});
    },

    setChoiceFilterOptions: (columnKey: string, options: ChoiceFilterOptions) => {
      const newCache = new Map(store.getState().choiceFilterOptionsCache);
      newCache.set(columnKey, options);
      store.setState({choiceFilterOptionsCache: newCache});
    },

    setSuggestionLabels: (suggestId: string, entries: SuggestionsOptions) => {
      if (isNilOrEmpty(entries)) {
        return;
      }
      const current = store.getState().suggestionLabelsCache;
      const nextInner = new Map(current.get(suggestId) ?? []);
      entries.forEach((entry) => nextInner.set(entry.value, entry.label));
      const nextCache = new Map(current);
      nextCache.set(suggestId, nextInner);
      store.setState({suggestionLabelsCache: nextCache});
    },

    setIsBulkActionLoading: (loading: boolean) => {
      if (store.getState().isBulkActionLoading === loading) {
        return;
      }
      store.setState({isBulkActionLoading: loading});
    },

    setSelectedCellRange: (range: SelectedCellRange | null) => {
      store.setState({selectedCellRange: range});
    },
  };
}
